<template>
  <a-modal
    title="动画截取和定格"
    :visible="visible"
    @cancel="close"
    width="722px"
    @ok="handleOK"
    :maskClosable="false"
    class="video-cropper-modal lottie-crop-modal"
  >
    <a-radio-group v-model="paused" button-style="solid">
      <a-radio-button :value="false">截取</a-radio-button>
      <a-radio-button :value="true">定格</a-radio-button>
    </a-radio-group>
    <div
      class="video-cropper-modal__video"
      ref="player"
      :style="{ height: lottieHeight + 'px' }"
    />
    <el-slider
      v-if="paused"
      v-model="start"
      :min="0"
      :max="totalDuration"
      :step="0.01"
      :formatTooltip="formatDuration"
    ></el-slider>
    <div class="video-cropper-container" v-else>
      <div class="video-cropper-container__start">00:00.00</div>
      <div class="video-cropper-container__end">
        {{ formatDuration(totalDuration) }}
      </div>
      <el-slider
        class="slide-container__slider video-cropper-container__item"
        :min="0"
        :max="totalDuration"
        :step="0.01"
        v-model="current"
        :disabled="playing"
        :formatTooltip="formatDuration"
      />
      <div class="video-cropper-container__body video-cropper-container__item">
        <div
          class="track"
          :class="{ 'track--selected': dragging }"
          :style="trackStyle"
          @mousedown="handleMouseDown"
        >
          <div
            class="track__handle track__handle--start"
            @mousedown.stop="handleStartMouseDown"
          />
          <div
            class="track__handle track__handle--end"
            @mousedown.stop="handleEndMouseDown"
          />
        </div>
      </div>
    </div>

    <a-row type="flex" align="middle" gutter="8">
      <a-col>
        <a-input-number
          class="video-cropper-modal__input"
          v-model="displayStart"
          :step="0.001"
          :precision="3"
          :min="0"
          :max="totalDuration - 0.4"
          @blur="handleStartInputChange"
          @keydown.native.13="handleStartInputChange"
        />
      </a-col>
      <template v-if="!paused">
        <a-col>&ndash;</a-col>
        <a-col>
          <a-input-number
            class="video-cropper-modal__input"
            v-model="displayEnd"
            :step="0.001"
            :max="totalDuration"
            :min="0.4"
            :precision="3"
            @blur="handleEndInputChange"
            @keydown.native.13="handleEndInputChange"
          />
        </a-col>
      </template>
      <a-col flex="1 0 100px">
        <template v-if="!paused">
          截取后时长: {{ formatDuration(duration) }}
        </template>
      </a-col>
      <a-col v-if="!paused">
        <a-button
          type="primary"
          class="video-cropper-modal__btn"
          @click="togglePlay"
        >
          {{ playing ? "暂停" : "播放" }}
        </a-button>
      </a-col>
      <a-col>
        <a-button class="video-cropper-modal__btn" @click="reset"
          >重置</a-button
        >
      </a-col>
    </a-row>
  </a-modal>
</template>
<script>
import { clamp, formatTimeInterval } from '../../utils';
import getLottie from '../../utils/getLottie';
import lottie from 'lottie-web';

export default {
  props: ['visible', 'src', 'value'],
  data() {
    return {
      current: 0,
      totalDuration: 10,
      start: 0,
      end: 0,
      playing: false,
      dragging: false,
      displayStart: 0,
      displayEnd: 0,
      lottieHeight: 0,
      paused: false
    };
  },
  computed: {
    duration() {
      return this.end - this.start;
    },
    trackStyle() {
      return {
        left: this.start * 100 / this.totalDuration + '%',
        width: `calc(${this.duration * 100 / this.totalDuration + '%'} + 22px)`,
        marginLeft: '-11px',
        padding: 0
      };
    },
    usedCurrent() {
      return this.paused ? this.start : this.current;
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false);
    },
    processPlay() {
      let currentProgress = (new Date() - this.playStart) / 1e3 + this.prevProgress;
      if (currentProgress < this.totalDuration && (this.prevProgress >= this.end || currentProgress < this.end)) {
        this.current = currentProgress;
        this.playAF = requestAnimationFrame(this.processPlay);
      } else {
        this.playing = false;
        this.current = this.start || 0;
      }
    },
    togglePlay() {
      if (!this.playing) {
        this.playing = true;
        this.playStart = +new Date();
        this.prevProgress = this.current;
        this.playAF = requestAnimationFrame(this.processPlay);
      } else {
        this.pause();
      }
    },
    pause() {
      this.playing = false;
      cancelAnimationFrame(this.playAF);
      this.playAF = null;
    },
    formatDuration(value) {
      const milliseconds = value * 1e3;
      return formatTimeInterval(milliseconds);
    },
    reset() {
      if (this.value) {
        this.start = this.value[0];
        this.end = this.value[1];
      } else {
        this.start = 0;
        this.end = this.totalDuration;
      }
    },
    handleMouseDown(e) {
      this.dragging = true;
      this.origPos = e.clientX;
      this.origStart = this.start;
      this.origEnd = this.end;
      document.body.classList.add('unselectable');
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    },
    handleMouseMove(e) {
      if (this.dragging) {
        const delta = clamp(
          (e.clientX - this.origPos) / 672 * this.totalDuration,
          -this.origStart,
          this.totalDuration - this.origEnd
        );
        this.start = this.origStart + delta;
        this.end = this.origEnd + delta;
      }
    },
    handleMouseUp() {
      if (this.dragging) {
        this.dragging = false;
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    },
    handleStartMouseDown(e) {
      this.startDragging = true;
      this.origPos = e.clientX;
      this.origStart = this.start;
      document.body.classList.add('unselectable');
      document.addEventListener('mousemove', this.handleStartMouseMove);
      document.addEventListener('mouseup', this.handleStartMouseUp);
    },
    handleStartMouseMove(e) {
      if (this.startDragging) {
        const delta = (e.clientX - this.origPos) / 672 * this.totalDuration;
        this.start = clamp(this.origStart + delta, 0, this.end - .4);
      }
    },
    handleStartMouseUp() {
      if (this.startDragging) {
        this.startDragging = false;
        document.removeEventListener('mousemove', this.handleStartMouseMove);
        document.removeEventListener('mouseup', this.handleStartMouseUp);
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    },
    handleEndMouseDown(e) {
      this.endDragging = true;
      this.origPos = e.clientX;
      this.origEnd = this.end;
      document.body.classList.add('unselectable');
      document.addEventListener('mousemove', this.handleEndMouseMove);
      document.addEventListener('mouseup', this.handleEndMouseUp);
    },
    handleEndMouseMove(e) {
      if (this.endDragging) {
        const delta = (e.clientX - this.origPos) / 672 * this.totalDuration;
        this.end = clamp(this.origEnd + delta, this.start + .4, this.totalDuration);
      }
    },
    handleEndMouseUp() {
      if (this.endDragging) {
        this.endDragging = false;
        document.removeEventListener('mousemove', this.handleEndMouseMove);
        document.removeEventListener('mouseup', this.handleEndMouseUp);
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    },
    handleOK() {
      this.$emit('change', {
        start: this.start,
        end: this.end,
        total: this.totalDuration
      });
      this.close();
    },
    handleStartInputChange() {
      const start = clamp(this.displayStart, 0, this.totalDuration - .4);
      if (start > this.end - .4) {
        this.end = Math.min(start + .4, this.totalDuration);
      }
      this.start = start;
    },
    handleEndInputChange() {
      const end = clamp(this.displayEnd, .4, this.totalDuration);
      if (end < this.start + .4) {
        this.start = Math.max(end - .4, 0);
      }
      this.end = end;
    },
    async loadLottie() {
      if (this.lottie) {
        this.lottie.destroy();
      }
      if (this.src) {
        const lottieFile = await getLottie(this.src);
        const assetsPath = this.src.replace(/[^/]+$/, '') + 'images/';

        this.lottieHeight = 674 * lottieFile.h / lottieFile.w;

        this.lottie = lottie.loadAnimation({
          container: this.$refs.player,
          renderer: 'svg',
          autoplay: false,
          name: 'cropLottie',
          animationData: lottieFile,
          assetsPath
        });

        this.totalDuration = lottieFile.op / lottieFile.fr;
      }
    }
  },
  watch: {
    src() {
      this.loadLottie();
    },
    usedCurrent(val) {
      if (this.lottie) {
        this.lottie.goToAndStop(val * 1e3);
      }
    },
    visible(val) {
      if (val) {
        this.reset();
        this.$nextTick(async () => {
          this.displayStart = this.start;
          this.displayEnd = this.end;
          this.paused = this.start === this.end;
          if (!this.lottie) {
            await this.loadLottie();
          }

          this.lottie.goToAndStop(this.usedCurrent * 1e3);
        });
      }
    },
    start(val) {
      this.displayStart = val;
      if (this.paused) {
        this.end = val;
      }
    },
    end(val) {
      this.displayEnd = val;
    },
    paused(value) {
      if (value) {
        this.end = this.start;
      } else {
        this.start = clamp(this.start, 0, this.totalDuration - 1);
        this.end = this.start + 1;
        this.current = this.start;
      }
    }
  }
}
</script>
<style>
.lottie-crop-modal .ant-modal-body {
  padding-top: 16px;
}

.lottie-crop-modal .ant-radio-group {
  display: flex;
  margin: 0 auto 16px;
  width: 240px;
}

.lottie-crop-modal .ant-radio-button-wrapper {
  flex-grow: 1;
  text-align: center;
}
</style>